import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { IndianRupee, CheckCircle2, XCircle, Clock, Copy, Undo2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  paid: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800", label: "Paid" },
  created: { icon: Clock, cls: "bg-amber-100 text-amber-800", label: "Awaiting" },
  signature_failed: { icon: XCircle, cls: "bg-rose-100 text-rose-800", label: "Signature failed" },
  refunded: { icon: Undo2, cls: "bg-zinc-200 text-zinc-800", label: "Refunded" },
};

function fmtAmount(paise, inr) {
  const rupees = typeof inr === "number" ? inr : (paise || 0) / 100;
  return `₹${rupees.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}
function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function PaymentHistory({ taskId }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!taskId) return;
    setItems(null); setErr("");
    api.get("/payments", { params: { task_id: taskId } })
      .then(r => setItems(r.data))
      .catch(e => setErr(e.response?.data?.detail || e.message));
  }, [taskId]);

  const copy = async (text) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); toast.success("Copied", { description: text }); }
    catch { window.prompt("Copy:", text); }
  };

  const refund = async (p) => {
    if (!window.confirm(`Refund ₹${p.amount_inr?.toLocaleString("en-IN")} for order ${p.order_id}?\nThis calls the Razorpay refund API and cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/payments/${p.id}/refund`);
      toast.success("Refund initiated", { description: `Razorpay refund ${data.refund_id || ""} · ${data.status || "processing"}` });
      // reload
      const r = await api.get("/payments", { params: { task_id: taskId } });
      setItems(r.data);
    } catch (e) {
      toast.error("Refund failed", { description: e.response?.data?.detail || e.message });
    }
  };

  if (err) return <div className="text-xs text-rose-600">Could not load payment history: {err}</div>;
  if (items === null) return <div className="text-xs text-zinc-500">Loading payment history…</div>;
  if (!items.length) return (
    <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-300 p-3 text-center" data-testid={`payment-history-empty-${taskId}`}>
      No Razorpay attempts yet. Click the ₹ button on the task row to raise the first invoice.
    </div>
  );

  const paid = items.filter(p => p.status === "paid");
  const total = paid.reduce((a, p) => a + (p.amount_inr || 0), 0);

  return (
    <div className="space-y-3" data-testid={`payment-history-${taskId}`}>
      <div className="flex items-center gap-4 text-[11px] tracking-[0.14em] uppercase text-zinc-500 font-semibold">
        <span>Total attempts: <span className="tabular text-zinc-900 font-mono">{items.length}</span></span>
        <span>Paid: <span className="tabular text-emerald-700 font-mono">{paid.length}</span></span>
        <span>Collected: <span className="tabular text-zinc-900 font-mono">{fmtAmount(null, total)}</span></span>
      </div>
      <ul className="divide-y divide-zinc-200 border border-zinc-200 bg-white">
        {items.map(p => {
          const s = STATUS_STYLES[p.status] || { icon: Clock, cls: "bg-zinc-100 text-zinc-700", label: p.status || "unknown" };
          const Icon = s.icon;
          return (
            <li key={p.id} data-testid={`payment-row-${p.id}`} className="grid grid-cols-[24px_1fr_140px_120px] gap-3 px-3 py-2.5 items-center">
              <Icon size={16} className={p.status === "paid" ? "text-emerald-700" : p.status === "signature_failed" ? "text-rose-600" : "text-amber-600"} strokeWidth={1.75} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] truncate" title={p.order_id}>{p.order_id}</span>
                  <button aria-label="Copy order id" onClick={() => copy(p.order_id)} className="p-0.5 hover:bg-zinc-100 rounded"><Copy size={11} /></button>
                </div>
                {p.payment_id ? (
                  <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                    <span className="font-mono truncate" title={p.payment_id}>pay: {p.payment_id}</span>
                    <button aria-label="Copy payment id" onClick={() => copy(p.payment_id)} className="p-0.5 hover:bg-zinc-100 rounded"><Copy size={10} /></button>
                  </div>
                ) : (
                  <div className="text-[10px] text-zinc-500">{p.description}</div>
                )}
              </div>
              <div className="flex items-center gap-1 font-semibold tabular text-sm">
                <IndianRupee size={12} className="text-zinc-500" />
                {(p.amount_inr || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold ${s.cls}`}>{s.label}</span>
                <span className="text-[10px] text-zinc-500 tabular">{fmtWhen(p.refunded_at || p.paid_at || p.created_at)}</span>
                {p.status === "paid" ? (
                  <button
                    data-testid={`refund-${p.id}`}
                    onClick={() => refund(p)}
                    className="text-[10px] tracking-widest uppercase font-semibold text-rose-700 hover:underline"
                  >
                    Refund
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
